import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { useUserStore } from "../hooks/useMyUserId";

type ViewStateMemoryStore = {
  ownerUserId: string | null;
  entries: Map<string, unknown>;
};

export interface AtomViewStateMemory {
  has: (key: string) => boolean;
  get: (key: string) => unknown;
  set: (key: string, value: unknown) => void;
}

const AtomViewStateContext = createContext<AtomViewStateMemory | null>(null);

/**
 * In-memory view state (table filters, sorting, …) that survives SPA
 * navigation. It backs the `"memory"` persistence level and caches the
 * `"browser"` one; values are gone after a page reload and are dropped as
 * soon as a different user signs in.
 * Place this at the root of your app; AtomFrameworkProvider already does.
 */
export function AtomViewStateProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  // Use ref to avoid re-renders when view state changes.
  const storeRef = useRef<ViewStateMemoryStore>({
    ownerUserId: null,
    entries: new Map(),
  });

  // View state may hold user-entered search terms, so never hand one user's
  // values to the next, even if the auth flow switches users without a
  // reload or remount.
  const getEntries = useCallback((): Map<string, unknown> => {
    const { userId } = useUserStore.getState();
    if (storeRef.current.ownerUserId !== userId) {
      storeRef.current = { ownerUserId: userId, entries: new Map() };
    }
    return storeRef.current.entries;
  }, []);

  const value = useMemo<AtomViewStateMemory>(
    () => ({
      has: (key) => getEntries().has(key),
      get: (key) => getEntries().get(key),
      set: (key, entry) => {
        getEntries().set(key, entry);
      },
    }),
    [getEntries],
  );

  return (
    <AtomViewStateContext.Provider value={value}>
      {children}
    </AtomViewStateContext.Provider>
  );
}

/**
 * The in-memory view state store, or null outside of AtomViewStateProvider.
 */
export function useAtomViewStateMemory(): AtomViewStateMemory | null {
  return useContext(AtomViewStateContext);
}
